// UpgradeScreen — proper Free vs Pro comparison + checkout entry point.
//
// v0.45.19 — Brett (with screenshot of the UpgradePrompt → window.alert):
//   "i thought the page was made after this with comparisons subscription
//    etc."
//
// The signup flow (v0.45.17) already had a side-by-side plan picker.
// In-app upgrade was missing the equivalent — UpgradePrompt's "Get Pro"
// button only fired a JS alert. This screen is the in-app counterpart:
// a real /upgrade route with the same Free vs Pro feature comparison
// the signup picker shows, plus contextual messaging when the user lands
// here from a specific limit hit.
//
// Route: /upgrade?reason=<key>     reason is optional; when set the
//                                  page renders a banner explaining
//                                  which limit triggered the upgrade.
//
// Stripe checkout is queued. For now "Get Pro" opens a sales mailto
// with a pre-filled subject so the operator can capture the lead and
// convert manually. As soon as Stripe lands, this screen swaps in the
// Checkout redirect — same comparison page, real checkout button.

import React from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { theme, card, btnPrimary, btnSecondary } from '../theme';
import { Check, X as XIcon, Sparkles, Building2, ArrowLeft } from 'lucide-react';
import { FREE_LIMITS, limitMessage, isProTier } from '../utils/tier';
import DropPinWordmark from '../components/DropPinWordmark';

// PLAN_SPECS — same shape as the LoginScreen signup picker so the two
// surfaces stay visually consistent. (When the signup picker eventually
// imports from a shared module instead of duplicating, this is the
// module.)
const PLAN_SPECS = {
  free: {
    name:     'Free',
    price:    'AU$0',
    cadence:  'forever',
    headline: 'Try the full flow on one project.',
    features: [
      { ok: true,  text: `${FREE_LIMITS.activeProjects} active project` },
      { ok: true,  text: `${FREE_LIMITS.defectsPerProject} defects per project` },
      { ok: true,  text: `${FREE_LIMITS.drawingsPerReport} drawing per report` },
      { ok: true,  text: `${FREE_LIMITS.reportsPerMonth} report shares per month` },
      { ok: true,  text: `${FREE_LIMITS.photoStorageMB} MB photo storage` },
      { ok: true,  text: 'PDF reports (with DropPin watermark)' },
      { ok: false, text: 'White-label PDFs' },
      { ok: false, text: 'Public share links' },
      { ok: false, text: 'Invite subbies to log in' },
    ],
    cta: 'Stay on Free',
  },
  pro: {
    name:     'Pro',
    price:    'AU$29',
    cadence:  'per user / month',
    headline: 'Everything, no caps. Send unlimited reports.',
    features: [
      { ok: true, text: 'Unlimited active projects' },
      { ok: true, text: 'Unlimited defects per project' },
      { ok: true, text: 'Unlimited drawings per report' },
      { ok: true, text: 'Unlimited report shares' },
      { ok: true, text: '10 GB photo storage' },
      { ok: true, text: 'White-label PDF (your logo, no watermark)' },
      { ok: true, text: 'Public share links to clients & builders' },
      { ok: true, text: 'Invite subbies — they see only their items' },
      { ok: true, text: 'Priority email support' },
    ],
    cta: 'Get Pro',
    badge: 'Most popular',
  },
};

export default function UpgradeScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');
  const reasonMsg = reason ? limitMessage(reason) : null;
  const alreadyPro = isProTier();

  // Stripe wiring is queued. For now mail-to with a structured subject
  // so the sales mailbox can match the lead to the limit they hit.
  const handleGetPro = () => {
    const subj = encodeURIComponent(
      reason ? `DropPin Pro upgrade — hit ${reason} limit` : 'DropPin Pro upgrade request'
    );
    const body = encodeURIComponent(
      `Hi DropPin team,\n\nI'd like to upgrade to Pro.\n\n${reason ? `Triggered by: ${reasonMsg}\n\n` : ''}Thanks.`
    );
    window.location.href = `mailto:sales@insitebg.com.au?subject=${subj}&body=${body}`;
  };

  return (
    <div style={{ maxWidth: 1020, margin: '0 auto', padding: '8px 4px 32px' }}>
      {/* Back link — gets the user out without committing to anything */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          background: 'transparent', border: 'none',
          color: theme.textLight, fontSize: 13, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: 0, marginBottom: 10, fontFamily: 'inherit',
        }}
      >
        <ArrowLeft size={14} /> Back
      </button>

      {/* Hero — wordmark, headline, why-now banner if a reason was passed */}
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.brand, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
          <DropPinWordmark /> Pro
        </div>
        <h1 style={{ margin: '0 0 6px', fontSize: 28, fontWeight: 800, letterSpacing: '-0.01em', color: theme.text }}>
          Compare plans
        </h1>
        <div style={{ fontSize: 14, color: theme.textLight }}>
          Change or cancel any time. Free has no time limit.
        </div>
      </div>

      {alreadyPro && (
        <div style={{
          background: theme.bg,
          border: `1px solid ${theme.success}`,
          color: theme.success,
          padding: '10px 14px',
          borderRadius: 8,
          marginBottom: 14,
          fontSize: 13,
          fontWeight: 600,
          textAlign: 'center',
        }}>
          You're already on Pro. This page is here for reference.
        </div>
      )}

      {reasonMsg && !alreadyPro && (
        <div style={{
          background: theme.bg,
          border: `1px solid ${theme.brand}`,
          color: theme.text,
          padding: '10px 14px',
          borderRadius: 8,
          marginBottom: 14,
          fontSize: 13,
        }}>
          <strong style={{ display: 'block', marginBottom: 2 }}>Why you're here</strong>
          {reasonMsg}
        </div>
      )}

      {/* Side-by-side plan cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 14,
      }}>
        <PlanCard
          plan={PLAN_SPECS.free}
          accent={theme.text}
          icon={<Building2 size={18} />}
          current={!alreadyPro}
          onClick={() => navigate(-1)}
        />
        <PlanCard
          plan={PLAN_SPECS.pro}
          accent={theme.brand}
          icon={<Sparkles size={18} />}
          current={alreadyPro}
          highlight
          onClick={alreadyPro ? null : handleGetPro}
        />
      </div>

      {/* Comparison table — the long-format spec view, in case the
          cards leave any feature unclear */}
      <div style={{ ...card, marginTop: 18, padding: 0, overflow: 'hidden' }}>
        <div style={{
          padding: '12px 16px',
          fontSize: 14, fontWeight: 800,
          background: theme.surface,
          borderBottom: `1px solid ${theme.border}`,
        }}>
          Full feature comparison
        </div>
        <ComparisonTable />
      </div>

      <div style={{
        marginTop: 18, padding: '14px 16px',
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        fontSize: 13, color: theme.textLight, lineHeight: 1.55,
      }}>
        <strong style={{ color: theme.text }}>Questions about Pro?</strong>{' '}
        Email <a href="mailto:sales@insitebg.com.au" style={{ color: theme.brand, fontWeight: 600 }}>
          sales@insitebg.com.au
        </a>{' '}— same address as the Get Pro button. We'll come back within one
        business day. (Stripe self-serve checkout is shipping soon; for now we
        set you up manually so you start on the right tenant.)
      </div>
    </div>
  );
}

// ── Plan card ─────────────────────────────────────────────────────────

function PlanCard({ plan, accent, icon, current, highlight, onClick }) {
  return (
    <div
      style={{
        ...card,
        position: 'relative',
        padding: 20,
        border: `2px solid ${highlight ? accent : theme.border}`,
        boxShadow: highlight ? `0 8px 24px ${accent}22` : undefined,
      }}
    >
      {plan.badge && (
        <div style={{
          position: 'absolute', top: -10, right: 14,
          background: accent, color: '#fff',
          fontSize: 10, fontWeight: 700, padding: '3px 8px',
          borderRadius: 99, textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>{plan.badge}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: accent }}>
        {icon}
        <div style={{ fontSize: 20, fontWeight: 800 }}>{plan.name}</div>
        {current && (
          <span style={{
            marginLeft: 'auto',
            fontSize: 10, fontWeight: 700, padding: '2px 7px',
            borderRadius: 99, background: theme.bg, color: theme.success,
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>Current</span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 6 }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: theme.text }}>{plan.price}</div>
        <div style={{ fontSize: 12, color: theme.textLight }}>{plan.cadence}</div>
      </div>
      <div style={{ fontSize: 13, color: theme.textLight, marginTop: 4 }}>{plan.headline}</div>

      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {plan.features.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 13 }}>
            {f.ok ? (
              <Check size={15} style={{ color: theme.success, flexShrink: 0, marginTop: 1 }} strokeWidth={2.5} />
            ) : (
              <XIcon size={15} style={{ color: theme.textLight, flexShrink: 0, marginTop: 1 }} strokeWidth={2.5} />
            )}
            <span style={{ color: f.ok ? theme.text : theme.textLight }}>{f.text}</span>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onClick || undefined}
        disabled={!onClick}
        style={{
          marginTop: 16,
          width: '100%',
          background: highlight ? accent : 'transparent',
          color: highlight ? '#fff' : accent,
          border: `1.5px solid ${accent}`,
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 14, fontWeight: 700,
          cursor: onClick ? 'pointer' : 'default',
          opacity: onClick ? 1 : 0.5,
          fontFamily: 'inherit',
        }}
      >
        {current ? `You're on ${plan.name}` : plan.cta}
      </button>
    </div>
  );
}

// ── Detailed comparison table ─────────────────────────────────────────

function ComparisonTable() {
  // Rows: [feature, free value, pro value]. Booleans render as
  // check / cross; strings render as-is.
  const rows = [
    ['Active projects',             `${FREE_LIMITS.activeProjects}`,         'Unlimited'],
    ['Defects per project',         `${FREE_LIMITS.defectsPerProject}`,      'Unlimited'],
    ['Drawings per report',         `${FREE_LIMITS.drawingsPerReport}`,      'Unlimited'],
    ['Report shares per month',     `${FREE_LIMITS.reportsPerMonth}`,        'Unlimited'],
    ['Photo storage',               `${FREE_LIMITS.photoStorageMB} MB`,      '10 GB'],
    ['PDF reports',                  true,  true],
    ['White-label PDF',              false, true],
    ['Custom brand colour + logo',   false, true],
    ['Public share links',           false, true],
    ['Invite subbies to log in',     false, true],
    ['Trade-coloured pins',          true,  true],
    ['Plan-page rendering',          true,  true],
    ['AI ✨ Location suggestion',     true,  true],
    ['Audit trail',                  true,  true],
    ['Priority email support',       false, true],
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ background: theme.surface, color: theme.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <th style={{ textAlign: 'left',  padding: '8px 16px', fontWeight: 700, borderBottom: `1px solid ${theme.border}` }}>Feature</th>
          <th style={{ textAlign: 'center', padding: '8px 16px', fontWeight: 700, borderBottom: `1px solid ${theme.border}` }}>Free</th>
          <th style={{ textAlign: 'center', padding: '8px 16px', fontWeight: 700, borderBottom: `1px solid ${theme.border}`, color: theme.brand }}>Pro</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, fv, pv], i) => (
          <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
            <td style={{ padding: '9px 16px', color: theme.text }}>{label}</td>
            <td style={{ padding: '9px 16px', textAlign: 'center' }}><Cell value={fv} /></td>
            <td style={{ padding: '9px 16px', textAlign: 'center' }}><Cell value={pv} pro /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Cell({ value, pro }) {
  if (value === true)  return <Check size={16} style={{ color: pro ? theme.brand : theme.success }} strokeWidth={2.5} />;
  if (value === false) return <XIcon size={16} style={{ color: theme.textLight }} strokeWidth={2.5} />;
  return <span style={{ color: pro ? theme.text : theme.textLight, fontWeight: pro ? 700 : 400 }}>{value}</span>;
}
